// ============================================================================
// DRAWING FOLDER TREE - Hierarchical folder navigation
// Date: January 2026
// ============================================================================

'use client';

import React, { useState } from 'react';
import {
  Folder,
  FolderOpen,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Edit2,
  Trash2,
  FolderInput,
  FileText
} from 'lucide-react';
import type { DrawingFolder } from './types';
import { confirm } from '@/lib/notifications';

interface DrawingFolderTreeProps {
  folders: DrawingFolder[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: (parentId: string | null, name: string) => void;
  onRenameFolder: (folderId: string, name: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
  isDark?: boolean;
}

interface FolderNodeProps {
  folder: DrawingFolder;
  level: number;
  selectedFolderId: string | null;
  expandedFolders: Set<string>;
  onToggleExpand: (folderId: string) => void;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: (parentId: string | null, name: string) => void;
  onRenameFolder: (folderId: string, name: string) => void;
  onDeleteFolder: (folderId: string) => void;
  isDark?: boolean;
}

function FolderNode({
  folder,
  level,
  selectedFolderId,
  expandedFolders,
  onToggleExpand,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  isDark
}: FolderNodeProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(folder.name);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const isExpanded = expandedFolders.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;

  const handleRename = () => {
    if (newName.trim() && newName !== folder.name) {
      onRenameFolder(folder.id, newName.trim());
    }
    setIsRenaming(false);
  };

  const handleCreateSubfolder = () => {
    if (newFolderName.trim()) {
      onCreateFolder(folder.id, newFolderName.trim());
      setNewFolderName('');
      setShowNewFolder(false);
      if (!isExpanded) {
        onToggleExpand(folder.id);
      }
    }
  };

  return (
    <div>
      <div
        className={`group flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer transition-colors ${
          isSelected
            ? 'bg-blue-100 dark:bg-blue-900/30'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectFolder(folder.id)}
      >
        {/* Expand/collapse */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(folder.id);
          }}
          className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            hasChildren ? 'visible' : 'invisible'
          }`}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
        ) : (
          <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
        )}

        {/* Name */}
        {isRenaming ? (
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
              if (e.key === 'Escape') {
                setNewName(folder.name);
                setIsRenaming(false);
              }
            }}
            className={`flex-1 px-1 py-0.5 text-sm border rounded ${
              isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
            }`}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`flex-1 text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {folder.name}
          </span>
        )}

        {/* Drawing count */}
        {folder.drawing_count > 0 && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
            {folder.drawing_count}
          </span>
        )}

        {/* Actions menu */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className={`absolute right-0 top-full mt-1 w-40 py-1 rounded-md shadow-lg z-20 ${
                isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNewFolder(true);
                    setShowMenu(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <FolderPlus className="w-4 h-4" />
                  New Subfolder
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRenaming(true);
                    setShowMenu(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <Edit2 className="w-4 h-4" />
                  Rename
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if ((await confirm({ message: `Delete folder "${folder.name}" and all its contents?`, variant: 'danger' }))) {
                      onDeleteFolder(folder.id);
                    }
                    setShowMenu(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New subfolder input */}
      {showNewFolder && (
        <div
          className="flex items-center gap-2 py-1.5 px-2"
          style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
        >
          <FolderPlus className="w-4 h-4 text-amber-500" />
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onBlur={() => {
              if (!newFolderName.trim()) setShowNewFolder(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateSubfolder();
              if (e.key === 'Escape') {
                setNewFolderName('');
                setShowNewFolder(false);
              }
            }}
            placeholder="Folder name..."
            className={`flex-1 px-2 py-1 text-sm border rounded ${
              isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
            }`}
            autoFocus
          />
        </div>
      )}

      {/* Children */}
      {isExpanded && folder.children && folder.children.length > 0 && (
        <div>
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
              onSelectFolder={onSelectFolder}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              isDark={isDark}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DrawingFolderTree({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
  isDark
}: DrawingFolderTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showNewRootFolder, setShowNewRootFolder] = useState(false);
  const [newRootFolderName, setNewRootFolderName] = useState('');

  const handleToggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleCreateRootFolder = () => {
    if (newRootFolderName.trim()) {
      onCreateFolder(null, newRootFolderName.trim());
      setNewRootFolderName('');
      setShowNewRootFolder(false);
    }
  };

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <span className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          Folders
        </span>
        <button
          onClick={() => setShowNewRootFolder(true)}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          title="New Folder"
        >
          <FolderPlus className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* All Drawings */}
      <div
        className={`flex items-center gap-2 py-2 px-3 cursor-pointer transition-colors ${
          selectedFolderId === null
            ? 'bg-blue-100 dark:bg-blue-900/30'
            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        onClick={() => onSelectFolder(null)}
      >
        <FileText className="w-4 h-4 text-blue-500" />
        <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          All Drawings
        </span>
      </div>

      {/* Folder tree */}
      <div className="flex-1 overflow-y-auto py-1">
        {/* New root folder input */}
        {showNewRootFolder && (
          <div className="flex items-center gap-2 py-1.5 px-3">
            <FolderPlus className="w-4 h-4 text-amber-500" />
            <input
              type="text"
              value={newRootFolderName}
              onChange={(e) => setNewRootFolderName(e.target.value)}
              onBlur={() => {
                if (!newRootFolderName.trim()) setShowNewRootFolder(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateRootFolder();
                if (e.key === 'Escape') {
                  setNewRootFolderName('');
                  setShowNewRootFolder(false);
                }
              }}
              placeholder="Folder name..."
              className={`flex-1 px-2 py-1 text-sm border rounded ${
                isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
              }`}
              autoFocus
            />
          </div>
        )}

        {/* Folders */}
        {folders.map((folder) => (
          <FolderNode
            key={folder.id}
            folder={folder}
            level={0}
            selectedFolderId={selectedFolderId}
            expandedFolders={expandedFolders}
            onToggleExpand={handleToggleExpand}
            onSelectFolder={onSelectFolder}
            onCreateFolder={onCreateFolder}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
            isDark={isDark}
          />
        ))}

        {folders.length === 0 && !showNewRootFolder && (
          <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No folders yet</p>
            <button
              onClick={() => setShowNewRootFolder(true)}
              className="mt-2 text-sm text-blue-500 hover:underline"
            >
              Create your first folder
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
